import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  LinearProgress,
  Autocomplete
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Upload as UploadIcon,
  Code as CodeIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { webCategoryService } from '../../services/webCategoryService';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';

// ─── Empty row template ──────────────────────────────────────────────────────

const EMPTY_ROW = () => ({
  // Basic
  name: '',
  slug: '',
  location: '',
  image: '',
  isActive: true,
  order: 0,
  directLocationPage: false,
  // SEO
  metaTitle: '',
  metaDescription: '',
  // Content
  heading: '',
  subheading: '',
  description: '',
  // Hero Content
  heroContent: { title: '', description: '' },
  // Service Highlights
  serviceHighlights: {
    title: '',
    title2: '',
    points: [{ title: '', description: '' }]
  },
  // Why Choose Us
  whyChooseUs: {
    title: '',
    points: [{ title: '', description: '' }]
  },
  // How It Works
  howItWorks: {
    title: '',
    title2: '',
    steps: ['']
  },
  // Service Area
  serviceArea: { title: '', description: '' },
  // Call To Action
  callToAction: { title: '', description: '' },
  // Services list
  serviceTitle: '',
  services: [{ name: '', description: '', image: '' }],
  // Page (NCR shape)
  page: {
    heading: '',
    subheading: '',
    intro: '',
    overview: { title: '', description: '' }
  },
  // Meta (NCR shape)
  meta: { title: '', description: '' },
  // Location page extras
  subHeading: '',
  population: '',
  sites: '',
  map: '',
  heading3: '',
  heading4: '',
  para1: '',
  para2: '',
  _slugTouched: false
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toSlug = (str) =>
  (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const parseJsCode = (code) => {
  let c = code.trim();
  // Strip "export const varName =" prefix
  c = c.replace(/^export\s+const\s+\w+\s*=\s*/, '');
  // Strip trailing semicolon
  c = c.replace(/;\s*$/, '').trim();
  // eslint-disable-next-line no-new-func
  return new Function('return (' + c + ')')();
};

const normalizeItem = (item) => {
  const base = EMPTY_ROW();

  // Basic
  base.name = item.name || '';
  base.slug = item.slug || '';
  base.location = item.location || '';
  base.image = item.image || '';
  base.isActive = item.isActive !== undefined ? item.isActive : true;
  base.order = item.order || 0;
  base.directLocationPage = item.directLocationPage || false;

  // SEO — handle both flat and nested meta{}
  base.metaTitle = item.metaTitle || item.meta?.title || '';
  base.metaDescription = item.metaDescription || item.meta?.description || '';
  if (item.meta) base.meta = { title: item.meta.title || '', description: item.meta.description || '' };

  // Heading — handle both flat and page.heading
  base.heading = item.heading || item.page?.heading || '';
  base.subheading = item.subheading || item.page?.subheading || item.subHeading || '';
  base.description = item.description || '';

  // Hero
  if (item.heroContent) base.heroContent = { title: item.heroContent.title || '', description: item.heroContent.description || '' };

  // Service Highlights
  if (item.serviceHighlights) {
    base.serviceHighlights = {
      title: item.serviceHighlights.title || '',
      title2: item.serviceHighlights.title2 || '',
      points: Array.isArray(item.serviceHighlights.points) && item.serviceHighlights.points.length
        ? item.serviceHighlights.points.map(p => ({ title: p.title || '', description: p.description || '' }))
        : [{ title: '', description: '' }]
    };
  }

  // Why Choose Us
  if (item.whyChooseUs) {
    base.whyChooseUs = {
      title: item.whyChooseUs.title || '',
      points: Array.isArray(item.whyChooseUs.points) && item.whyChooseUs.points.length
        ? item.whyChooseUs.points.map(p => ({ title: p.title || '', description: p.description || '' }))
        : [{ title: '', description: '' }]
    };
  }

  // How It Works
  if (item.howItWorks) {
    base.howItWorks = {
      title: item.howItWorks.title || '',
      title2: item.howItWorks.title2 || '',
      steps: Array.isArray(item.howItWorks.steps) && item.howItWorks.steps.length
        ? item.howItWorks.steps
        : ['']
    };
  }

  // Service Area
  if (item.serviceArea) base.serviceArea = { title: item.serviceArea.title || '', description: item.serviceArea.description || '' };

  // Call To Action
  if (item.callToAction) base.callToAction = { title: item.callToAction.title || '', description: item.callToAction.description || '' };

  // Services list
  base.serviceTitle = item.serviceTitle || '';
  if (Array.isArray(item.services) && item.services.length) {
    base.services = item.services.map(s => ({ name: s.name || '', description: s.description || '', image: s.image || '' }));
  }

  // Page (NCR shape)
  if (item.page) {
    base.page = {
      heading: item.page.heading || '',
      subheading: item.page.subheading || '',
      intro: item.page.intro || '',
      overview: { title: item.page.overview?.title || '', description: item.page.overview?.description || '' }
    };
  }

  // Location page extras
  base.subHeading = item.subHeading || '';
  base.population = item.population || '';
  base.sites = item.sites || '';
  base.map = item.map || '';
  base.heading3 = item.heading3 || '';
  base.heading4 = item.heading4 || '';
  base.para1 = item.para1 || '';
  base.para2 = item.para2 || '';

  base._slugTouched = !!item.slug;
  return base;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, mb: 0.5, display: 'block' }}>
    {children}
  </Typography>
);

const PointsEditor = ({ points, onChange, label }) => {
  const add = () => onChange([...points, { title: '', description: '' }]);
  const remove = (i) => onChange(points.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = [...points];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  return (
    <Box>
      {points.map((pt, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <TextField size="small" fullWidth label={`${label} Title ${i + 1}`} value={pt.title} onChange={e => update(i, 'title', e.target.value)} sx={{ mb: 0.5 }} />
            <TextField size="small" fullWidth multiline rows={2} label={`${label} Description ${i + 1}`} value={pt.description} onChange={e => update(i, 'description', e.target.value)} />
          </Box>
          <IconButton size="small" color="error" onClick={() => remove(i)} disabled={points.length <= 1}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mt: 0.5 }}>
        Add {label}
      </Button>
    </Box>
  );
};

const StepsEditor = ({ steps, onChange }) => {
  const add = () => onChange([...steps, '']);
  const remove = (i) => onChange(steps.filter((_, idx) => idx !== i));
  const update = (i, val) => { const next = [...steps]; next[i] = val; onChange(next); };
  return (
    <Box>
      {steps.map((s, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField size="small" fullWidth multiline rows={2} label={`Step ${i + 1}`} value={s} onChange={e => update(i, e.target.value)} />
          <IconButton size="small" color="error" onClick={() => remove(i)} disabled={steps.length <= 1}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mt: 0.5 }}>Add Step</Button>
    </Box>
  );
};

const ServicesEditor = ({ services, onChange }) => {
  const add = () => onChange([...services, { name: '', description: '', image: '' }]);
  const remove = (i) => onChange(services.filter((_, idx) => idx !== i));
  const update = (i, field, val) => { const next = [...services]; next[i] = { ...next[i], [field]: val }; onChange(next); };
  return (
    <Box>
      {services.map((s, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Service {i + 1}</Typography>
            <IconButton size="small" color="error" onClick={() => remove(i)} disabled={services.length <= 1}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Service Name" value={s.name} onChange={e => update(i, 'name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Image URL" value={s.image} onChange={e => update(i, 'image', e.target.value)} /></Grid>
            <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Description" value={s.description} onChange={e => update(i, 'description', e.target.value)} /></Grid>
          </Grid>
        </Paper>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mt: 0.5 }}>Add Service</Button>
    </Box>
  );
};

// ─── Row Card ─────────────────────────────────────────────────────────────────

const RowCard = ({ row, idx, onChange, onRemove, totalRows }) => {
  const update = (field, value) => {
    const next = { ...row, [field]: value };
    if (field === 'name' && !next._slugTouched) next.slug = toSlug(value);
    onChange(idx, next);
  };
  const updateNested = (section, field, value) => {
    onChange(idx, { ...row, [section]: { ...row[section], [field]: value } });
  };
  const updateDeep = (section, subsection, field, value) => {
    onChange(idx, { ...row, [section]: { ...row[section], [subsection]: { ...(row[section]?.[subsection] || {}), [field]: value } } });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: 'divider' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`#${idx + 1}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {row.name || <span style={{ color: '#aaa' }}>Untitled Entry</span>}
            </Typography>
            {row.slug && <Chip label={row.slug} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
          </Box>
        }
        action={
          <Tooltip title="Remove this row">
            <span>
              <IconButton color="error" size="small" onClick={() => onRemove(idx)} disabled={totalRows <= 1}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
        sx={{ pb: 0, px: 2, pt: 1.5 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {/* ── Basic ── */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Basic Info</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField required size="small" fullWidth label="Name *" value={row.name} onChange={e => update('name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField required size="small" fullWidth label="Slug *" value={row.slug} onChange={e => { update('slug', e.target.value); onChange(idx, { ...row, slug: e.target.value, _slugTouched: true }); }} helperText="Auto-generated from name" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField size="small" fullWidth label="Location" value={row.location} onChange={e => update('location', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField size="small" fullWidth label="Image URL" value={row.image} onChange={e => update('image', e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField size="small" fullWidth type="number" label="Order" value={row.order} onChange={e => update('order', parseInt(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={6} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel control={<Switch checked={!!row.isActive} onChange={e => update('isActive', e.target.checked)} size="small" />} label="Active" />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel control={<Switch checked={!!row.directLocationPage} onChange={e => update('directLocationPage', e.target.checked)} size="small" />} label="Direct Location Page" />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── SEO ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>SEO / Meta</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Meta Title" value={row.metaTitle} onChange={e => update('metaTitle', e.target.value)} inputProps={{ maxLength: 60 }} InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" color={row.metaTitle.length > 55 ? 'error' : 'text.secondary'}>{row.metaTitle.length}/60</Typography></InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline rows={3} label="Meta Description" value={row.metaDescription} onChange={e => update('metaDescription', e.target.value)} inputProps={{ maxLength: 160 }} InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" color={row.metaDescription.length > 150 ? 'error' : 'text.secondary'}>{row.metaDescription.length}/160</Typography></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="meta.title (NCR shape)" value={row.meta?.title || ''} onChange={e => updateNested('meta', 'title', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="meta.description (NCR shape)" value={row.meta?.description || ''} onChange={e => updateNested('meta', 'description', e.target.value)} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Content ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Content</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Heading" value={row.heading} onChange={e => update('heading', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Subheading" value={row.subheading} onChange={e => update('subheading', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="SubHeading (location shape)" value={row.subHeading} onChange={e => update('subHeading', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Description" value={row.description} onChange={e => update('description', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Hero Content ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Hero Content</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField size="small" fullWidth label="Hero Title" value={row.heroContent?.title || ''} onChange={e => updateNested('heroContent', 'title', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={4} label="Hero Description" value={row.heroContent?.description || ''} onChange={e => updateNested('heroContent', 'description', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Service Highlights ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Service Highlights</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12}><TextField size="small" fullWidth label="Section Title" value={row.serviceHighlights?.title || ''} onChange={e => updateNested('serviceHighlights', 'title', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Section Subtitle (title2)" value={row.serviceHighlights?.title2 || ''} onChange={e => updateNested('serviceHighlights', 'title2', e.target.value)} /></Grid>
            </Grid>
            <SectionLabel>Points</SectionLabel>
            <PointsEditor
              label="Highlight"
              points={row.serviceHighlights?.points || [{ title: '', description: '' }]}
              onChange={pts => onChange(idx, { ...row, serviceHighlights: { ...row.serviceHighlights, points: pts } })}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── Why Choose Us ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Why Choose Us</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField size="small" fullWidth label="Section Title" value={row.whyChooseUs?.title || ''} onChange={e => updateNested('whyChooseUs', 'title', e.target.value)} sx={{ mb: 1.5 }} />
            <SectionLabel>Points</SectionLabel>
            <PointsEditor
              label="Reason"
              points={row.whyChooseUs?.points || [{ title: '', description: '' }]}
              onChange={pts => onChange(idx, { ...row, whyChooseUs: { ...row.whyChooseUs, points: pts } })}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── How It Works ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>How It Works</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12}><TextField size="small" fullWidth label="Section Title" value={row.howItWorks?.title || ''} onChange={e => updateNested('howItWorks', 'title', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Subtitle (title2)" value={row.howItWorks?.title2 || ''} onChange={e => updateNested('howItWorks', 'title2', e.target.value)} /></Grid>
            </Grid>
            <SectionLabel>Steps</SectionLabel>
            <StepsEditor
              steps={row.howItWorks?.steps || ['']}
              onChange={steps => onChange(idx, { ...row, howItWorks: { ...row.howItWorks, steps } })}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── Service Area ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Service Area</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField size="small" fullWidth label="Title" value={row.serviceArea?.title || ''} onChange={e => updateNested('serviceArea', 'title', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Description" value={row.serviceArea?.description || ''} onChange={e => updateNested('serviceArea', 'description', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Call To Action ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Call To Action</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField size="small" fullWidth label="Title" value={row.callToAction?.title || ''} onChange={e => updateNested('callToAction', 'title', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Description" value={row.callToAction?.description || ''} onChange={e => updateNested('callToAction', 'description', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Services List ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Services List</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField size="small" fullWidth label="Services Section Title" value={row.serviceTitle || ''} onChange={e => update('serviceTitle', e.target.value)} sx={{ mb: 1.5 }} />
            <ServicesEditor
              services={row.services || [{ name: '', description: '', image: '' }]}
              onChange={svcs => onChange(idx, { ...row, services: svcs })}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── Page (NCR shape) ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Page Object (NCR shape)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="page.heading" value={row.page?.heading || ''} onChange={e => updateNested('page', 'heading', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="page.subheading" value={row.page?.subheading || ''} onChange={e => updateNested('page', 'subheading', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="page.intro" value={row.page?.intro || ''} onChange={e => updateNested('page', 'intro', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="page.overview.title" value={row.page?.overview?.title || ''} onChange={e => updateDeep('page', 'overview', 'title', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline rows={2} label="page.overview.description" value={row.page?.overview?.description || ''} onChange={e => updateDeep('page', 'overview', 'description', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Location Extras ── */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Location Page Extras</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Population" value={row.population || ''} onChange={e => update('population', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Sites / Landmarks" value={row.sites || ''} onChange={e => update('sites', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth label="Google Maps Embed URL" value={row.map || ''} onChange={e => update('map', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Heading 3" value={row.heading3 || ''} onChange={e => update('heading3', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Heading 4" value={row.heading4 || ''} onChange={e => update('heading4', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Paragraph 1" value={row.para1 || ''} onChange={e => update('para1', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={3} label="Paragraph 2" value={row.para2 || ''} onChange={e => update('para2', e.target.value)} /></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const BulkImportWebCategory = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [rows, setRows] = useState([EMPTY_ROW()]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaderOpen, setLoaderOpen] = useState(true);
  const [loaderCode, setLoaderCode] = useState('');
  const [loaderError, setLoaderError] = useState('');
  const [errors, setErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importResult, setImportResult] = useState(null); // { updated, notFound[], errors[] }

  // ── Category selection ────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    setCategoriesLoading(true);
    webCategoryService.getParentCategoriesForDropdown()
      .then(res => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setCategories(list);
      })
      .catch(() => {})
      .finally(() => setCategoriesLoading(false));
  }, []);

  // ── Row handlers ──────────────────────────────────────────────────────────

  const handleRowChange = (idx, newRow) => {
    setRows(prev => prev.map((r, i) => (i === idx ? newRow : r)));
  };

  const handleAddRow = () => setRows(prev => [...prev, EMPTY_ROW()]);

  const handleRemoveRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  // ── JS Loader ─────────────────────────────────────────────────────────────

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setLoaderCode(evt.target.result);
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleParseAndFill = () => {
    setLoaderError('');
    try {
      const result = parseJsCode(loaderCode);
      if (!Array.isArray(result)) throw new Error('Parsed value is not an array. Make sure you paste a single array (e.g. the content of one export const).');
      setRows(result.map(normalizeItem));
      setLoaderOpen(false);
      setSnackbar({ open: true, message: `Loaded ${result.length} entries from JS data`, severity: 'success' });
    } catch (err) {
      setLoaderError(err.message);
    }
  };

  // ── Validation + Submit ───────────────────────────────────────────────────

  const validate = () => {
    const errs = [];
    if (!selectedCategory) errs.push('Please select a parent category before saving.');
    rows.forEach((row, idx) => {
      if (!row.name.trim()) errs.push(`Row ${idx + 1}: Name is required`);
      if (!row.slug.trim()) errs.push(`Row ${idx + 1}: Slug is required`);
    });
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setImporting(true);
    setImportResult(null);
    setProgress(10);
    try {
      const items = rows.map(({ _slugTouched, ...rest }) => ({
        ...rest,
        slug: rest.slug.toLowerCase().trim()
      }));
      setProgress(40);
      const categoryId = selectedCategory?.id || selectedCategory?.categoryId || selectedCategory?._id;
      const res = await webCategoryService.bulkImportCategories(items, categoryId);
      setProgress(100);
      const { updated = 0, notFound = [], errors: importErrors = [] } = res.data || {};
      setImportResult({ updated, notFound, errors: importErrors });
      const hasIssues = notFound.length > 0 || importErrors.length > 0;
      setSnackbar({
        open: true,
        message: `Import done: ${updated} updated${notFound.length ? `, ${notFound.length} not found` : ''}${importErrors.length ? `, ${importErrors.length} errors` : ''}`,
        severity: hasIssues ? 'warning' : 'success'
      });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || err.message || 'Import failed', severity: 'error' });
    } finally {
      setImporting(false);
      setProgress(0);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <PageHeaderLayout headerTitle="Bulk Import Web Categories">
        <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/admin/web-subcategory')}>
          Back
        </Button>
      </PageHeaderLayout>

      {/* Progress bar */}
      {importing && <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, borderRadius: 1 }} />}

      {/* ── Step 1: Select Parent Category ── */}
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, borderColor: selectedCategory ? 'success.main' : 'warning.main', bgcolor: selectedCategory ? 'success.50' : 'warning.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CategoryIcon color={selectedCategory ? 'success' : 'warning'} />
          <Typography variant="body2" fontWeight={700}>
            Step 1 — Select Parent Category
          </Typography>
          {selectedCategory && (
            <Chip icon={<CheckCircleIcon />} label={selectedCategory.title || selectedCategory.name || selectedCategory.categoryName} color="success" size="small" sx={{ ml: 1 }} />
          )}
          {!selectedCategory && (
            <Chip label="Required" color="warning" size="small" sx={{ ml: 1 }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          All imported entries will be <strong>updated</strong> by matching slug + category. If an entry does not exist yet, it will be skipped — create it first from the Web Categories page.
        </Typography>
        <Autocomplete
          options={categories}
          loading={categoriesLoading}
          value={selectedCategory}
          onChange={(_, val) => setSelectedCategory(val)}
          getOptionLabel={(opt) => opt?.title || opt?.name || opt?.categoryName || String(opt?.id || '')}
          isOptionEqualToValue={(opt, val) => (opt?.id || opt?._id) === (val?.id || val?._id)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Parent Category *"
              placeholder="Search and select a category..."
              error={errors.some(e => e.includes('category'))}
              InputProps={{ ...params.InputProps, endAdornment: (<>{categoriesLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
            />
          )}
          sx={{ maxWidth: 480 }}
        />
      </Paper>

      {/* ── Import Result Summary ── */}
      {importResult && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: (importResult.errors?.length || importResult.notFound?.length) ? 'warning.50' : 'success.50' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Import Result</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip icon={<UpdateIcon />} label={`${importResult.updated} Updated`} color="success" size="small" />
            {importResult.notFound?.length > 0 && <Chip label={`${importResult.notFound.length} Not Found`} color="warning" size="small" />}
            {importResult.errors?.length > 0 && <Chip label={`${importResult.errors.length} Errors`} color="error" size="small" />}
          </Box>
          {importResult.notFound?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', display: 'block', mb: 0.5 }}>
                The following entries were not found. Create them first from the Web Categories page:
              </Typography>
              {importResult.notFound.map((e, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 0.5, py: 0 }}>
                  <strong>{e.name}</strong> (slug: <code>{e.slug}</code>) — {e.reason}
                </Alert>
              ))}
            </Box>
          )}
          {importResult.errors?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark', display: 'block', mb: 0.5 }}>Errors:</Typography>
              {importResult.errors.map((e, i) => (
                <Alert key={i} severity="error" sx={{ mb: 0.5, py: 0 }}>
                  <strong>{e.slug}</strong>: {e.reason}
                </Alert>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* JS Loader panel */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          onClick={() => setLoaderOpen(o => !o)}
          sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', bgcolor: loaderOpen ? 'primary.50' : 'background.default', userSelect: 'none' }}
        >
          <CodeIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            Load from JS / JSON data
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Paste or upload an export const array
          </Typography>
          {loaderOpen ? <ExpandMoreIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />}
        </Box>

        <Collapse in={loaderOpen}>
          <Box sx={{ px: 2, pb: 2, pt: 1 }}>
            {loaderError && <Alert severity="error" sx={{ mb: 1.5 }}>{loaderError}</Alert>}
            <TextField
              fullWidth multiline rows={8}
              value={loaderCode}
              onChange={e => setLoaderCode(e.target.value)}
              placeholder={`Paste JS data here, e.g.:\nexport const beautyServicePunjab = [\n  { name: '...', slug: '...', ... },\n  ...\n]`}
              size="small"
              sx={{ mb: 1.5, fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" size="small" startIcon={<CodeIcon />} onClick={handleParseAndFill} disabled={!loaderCode.trim()}>
                Parse &amp; Fill Form
              </Button>
              <Button variant="outlined" size="small" startIcon={<AttachFileIcon />} onClick={() => fileInputRef.current?.click()}>
                Upload .js / .json file
              </Button>
              <Button variant="text" size="small" color="secondary" onClick={() => { setLoaderCode(''); setLoaderError(''); }}>
                Clear
              </Button>
            </Box>
            <input type="file" ref={fileInputRef} accept=".js,.json,.txt" style={{ display: 'none' }} onChange={handleFileUpload} />
          </Box>
        </Collapse>
      </Paper>

      {/* Validation errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}

      {/* Rows */}
      <Box>
        {rows.map((row, idx) => (
          <RowCard
            key={idx}
            row={row}
            idx={idx}
            onChange={handleRowChange}
            onRemove={handleRemoveRow}
            totalRows={rows.length}
          />
        ))}
      </Box>

      {/* Add row + Submit */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow}>
            Add Another Entry
          </Button>
          <Typography variant="caption" color="text.secondary">
            {rows.length} {rows.length === 1 ? 'entry' : 'entries'} total
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/admin/web-subcategory')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={importing || rows.length === 0 || !selectedCategory}
            title={!selectedCategory ? 'Select a parent category first' : ''}
          >
            {importing ? 'Saving...' : `Save ${rows.length} ${rows.length === 1 ? 'Entry' : 'Entries'}`}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkImportWebCategory;
